import React from 'react';
import { useParams } from 'react-router-dom';
import {
  Dropdown,
  DropdownItem,
  DropdownList,
  TrevorIcon,
} from '@trevor/ui-components';
import { Category, SavedQuery, UUID } from '@/types';
import { useAppSelector } from '@/store/hooks';
import AjaxSpinner from '@/components/AjaxSpinner';
import { selectSavedQuerySeriesId } from '@/features/joinModal/joinModalSlice';
import {
  getLayersSelectedViewSeriesId,
  getQueryBuilderFirstStep,
} from '@/store-selectors';
import { flow } from 'fp-ts/function';
import LayerFromSavedQuery from '@/components/layers/LayerFromSavedQuery';
import Layer from '@/components/layers/Layer';
import { useAutoFocus } from '@/hooks/useAutoFocus';
import { GetSavedQueriesResult, useGetSavedQueriesQuery } from './apiSlice';
import {
  FilterBar,
  FilterButton,
  SearchControlsContainer,
  SearchTermContainer,
  SearchTermInput,
  SelectedCategory,
} from './styled-components';
import { CategoryFilter, useSavedQueryFilter } from './useSavedQueryFilter';
import { SavedQueriesContainer } from './subcomponents';
import { SavedQueryWidgetRenderContext } from './types';
import {
  DASHBOARD_MODAL,
  DASHBOARD_WIDGET_CONFIGURATION,
  DEPENDANT_QUERIES_MODAL,
  JOIN_MODAL,
  SAVE_MODAL,
  START_FROM_SAVED_QUERY_DROPDOWN,
} from './widgetRenderContext.constants';
import { RenderModeProvider } from './context/RenderModeContext';

const getFirstStepParentSeriesId = (firstStep: Layer) =>
  firstStep instanceof LayerFromSavedQuery
    ? firstStep.parent.series
    : undefined;

const RENDER_CONTEXT_TO_SELECTED_QUERY_ID_SELECTOR_MAP: Record<
  SavedQueryWidgetRenderContext,
  F<any, Maybe<SavedQuery['id']>> // Replace any with RootState when properly defined
> = {
  [SAVE_MODAL]: getLayersSelectedViewSeriesId,
  [JOIN_MODAL]: selectSavedQuerySeriesId,
  [START_FROM_SAVED_QUERY_DROPDOWN]: flow(
    getQueryBuilderFirstStep,
    getFirstStepParentSeriesId,
  ),
  [DASHBOARD_MODAL]: () => null,
  [DEPENDANT_QUERIES_MODAL]: () => null,
  [DASHBOARD_WIDGET_CONFIGURATION]: () => null,
};

export type SavedQueriesWidgetProps = {
  onSelectQuery: F<SavedQuery>;
  mode: SavedQueryWidgetRenderContext;
};

export const SavedQueriesWidgetWithoutFetch: React.FC<
  SavedQueriesWidgetProps & {
    isLoading: boolean;
    data: GetSavedQueriesResult | undefined;
  }
> = ({ onSelectQuery, mode, isLoading, data }) => {
  const { datasourceId } = useParams<{ datasourceId: UUID }>();
  const autoFocusRef = useAutoFocus();

  const [
    filterTerm,
    filterType,
    category,
    setFilterTerm,
    setFilterType,
    setCategory,
    savedQueryPredicate,
  ] = useSavedQueryFilter();

  const selectedQuerySeriesId = useAppSelector(
    RENDER_CONTEXT_TO_SELECTED_QUERY_ID_SELECTOR_MAP[mode],
  );

  const filteredSavedQueries =
    data?.savedQueries?.filter?.(savedQueryPredicate) ?? [];

  const setDropdownCategory = (
    e: React.MouseEvent,
    newCategory: CategoryFilter,
  ) => {
    e.stopPropagation();
    setCategory(newCategory);
  };

  return (
    <>
      <FilterBar>
        <SearchTermContainer>
          <TrevorIcon name="search" $color="#666" />
          <SearchTermInput
            placeholder="Search queries"
            value={filterTerm}
            ref={autoFocusRef}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFilterTerm(e.target.value)
            }
          />
        </SearchTermContainer>
        <SearchControlsContainer>
          <FilterButton
            $active={filterType === 'ALL'}
            onClick={() => setFilterType('ALL')}
          >
            All
          </FilterButton>
          <FilterButton
            $active={filterType === 'MY_QUERIES'}
            onClick={() => setFilterType('MY_QUERIES')}
          >
            My Queries
          </FilterButton>
          <FilterButton
            $active={filterType === 'MY_STARRED'}
            onClick={() => setFilterType('MY_STARRED')}
          >
            My Starred
          </FilterButton>

          <Dropdown>
            <Dropdown.Trigger>
              <SelectedCategory>
                <span>
                  {category === 'ALL' ? 'All categories' : category.name}
                </span>

                <TrevorIcon name="dropdown-arrow-down" />
              </SelectedCategory>
            </Dropdown.Trigger>
            <Dropdown.Content top={36}>
              <DropdownList>
                <DropdownItem
                  selected={category === 'ALL'}
                  onClick={(e) => setDropdownCategory(e, 'ALL')}
                >
                  All categories
                </DropdownItem>
                {data?.categories.map((c) => (
                  <DropdownItem
                    key={c.id}
                    selected={(category as Category).id === c.id}
                    onClick={(e) => setDropdownCategory(e, c)}
                  >
                    {c.name}
                  </DropdownItem>
                ))}
              </DropdownList>
            </Dropdown.Content>
          </Dropdown>
        </SearchControlsContainer>
      </FilterBar>
      {isLoading ? (
        <AjaxSpinner />
      ) : (
        <RenderModeProvider mode={mode}>
          <SavedQueriesContainer
            datasourceId={datasourceId as UUID}
            onSelectQuery={onSelectQuery}
            savedQueries={filteredSavedQueries}
            selectedQuerySeriesId={selectedQuerySeriesId}
          />
        </RenderModeProvider>
      )}
    </>
  );
};

export const SavedQueriesWidget: React.FC<SavedQueriesWidgetProps> = (
  props,
) => {
  const { datasourceId } = useParams<{ datasourceId: UUID }>();
  const { data, isLoading } = useGetSavedQueriesQuery(datasourceId!, {
    refetchOnMountOrArgChange: 10,
  });

  return (
    <SavedQueriesWidgetWithoutFetch
      {...props}
      data={data}
      isLoading={isLoading}
    />
  );
};
